import React from 'react'
import { Link } from 'gatsby'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import { logEventContactFormSubmitted } from '../../common/ga'

import CountrySelector from '../CountrySelector'

const formSubmitUri = 'https://95fov9vfoc.execute-api.eu-west-1.amazonaws.com/dev/email'

const captions = {
  '': {
    title: 'Get in touch to see how Approvol can work for you',
    subtitle: 'Book your free 30-day trial today!',
    alternateContact: 'You can also contact us by sending an email to',
    disclaimer1: 'Your data will be stored and used to contact you regarding your request. Read our ',
    disclaimer2: ' on how we use your data.',
    privacyPolicy: 'privacy policy',
    labels: {
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      position: 'Position',
      company: 'Company',
      numberEmployees: 'Number of Employees',
      country: 'Country',
      message: 'Message',
    },
    errorMessages: {
      mandatory: {
        name: 'Please provide your name',
        email: 'Please provide your email address',
        phone: 'Please provide your phone number',
        position: 'Please provide your title/position',
        company: 'Please provide your company name',
        numberEmployees: 'Please provide your the approximate number of employees',
      },
      invalid: {
        email: 'Please make sure your email address is in the correct format',
        phone: 'Please make sure your phone number is in the correct format',
      },
    },
    buttonSubmit: 'Send',
  },
  'zh-cn': {
    title: '联系我们，了解 Approvol 如何为您服务',
    subtitle: '预定30天免费试用！',
    alternateContact: '您也可以通过发送邮件联系我们',
    disclaimer1: '我们将存储并使用您提供的数据来处理您的请求。请参考',
    disclaimer2: '以详细了解我们如何使用您的数据。',
    privacyPolicy: '隐私协议',
    labels: {
      name: '姓名',
      email: '邮件',
      phone: '电话',
      position: '职位',
      company: '公司',
      numberEmployees: '人员数量',
      country: '国家/地区',
      message: '留言',
    },
    errorMessages: {
      mandatory: {
        name: '请输入姓名',
        email: '请输入邮箱',
        phone: '请输入电话',
        position: '请输入职位',
        company: '请输入公司名称',
        numberEmployees: '请提供人员数量',
      },
      invalid: {
        email: '请确保邮件地址格式正确',
        phone: '请确保电话号码格式正确',
      },
    },
    buttonSubmit: '发送',
  },
}

const ContactFormInput = React.forwardRef(({
  name,
  lang,
  type,
  maxLength,
  errors,
  onChange,
  onBlur,
}, ref) => {
  return (
    <div className='field mb-4'>
      <label htmlFor={name} className='label has-text-weight-normal is-size-5-desktop is-size-6-touch mb-1'>
        {captions[lang].labels[name]} <span className='has-text-danger'>*</span>
      </label>
      <div className='control'>
        <input name={name} type={type} className='input' maxLength={maxLength}
          ref={ref} onChange={onChange} onBlur={onBlur}
        />
      </div>
      <p className='help is-danger'>{errors[name]?.message}</p>
    </div>
  )
})

const ContactForm = ({
  setStatus,
  lang,
}) => {
  const { register, handleSubmit, formState: { errors } } = useForm({mode: 'onBlur'})

  const onSubmit = async (data) => {
    const body = {
      name: data.name || '',
      phone: data.phone || '',
      email: data.email || '',
      position: data.position || '',
      company: data.company || '',
      numberEmployees: parseInt(data.numberEmployees) || 0,
      country: data.country || '',
      message: data.message || '',
    }

    try {
      logEventContactFormSubmitted()

      const res = await axios.request({
        method: 'POST',
        url: formSubmitUri,
        data: body,
      })

      if (res.status === 200) {
        setStatus(true)
      } else {
        throw new Error('HTTP Status ' + res.status)
      }
    } catch (err) {
      console.error(err)
      setStatus(false)
    }
  }

  const langUriPrefix = lang ? '/' + lang : ''

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className='columns is-multiline'>
        <div className='column is-full has-text-centered title'>
          <h1 className='is-size-3-desktop is-size-4-touch has-text-grey'>
            {captions[lang].title}
          </h1>
        </div>
        <div className='column is-half'>
          <ContactFormInput name='name' lang={lang} type='text' maxLength='50' errors={errors}
            {...register('name', {required: {value: true, message: captions[lang].errorMessages.mandatory.name}})} />
          <ContactFormInput name='email' lang={lang} type='email' maxLength='100' errors={errors}
            {...register('email', {required: {value: true, message: captions[lang].errorMessages.mandatory.email}, pattern: {value: /^.+@.+\..+$/i, message: captions[lang].errorMessages.invalid.email}})} />
          <ContactFormInput name='phone' lang={lang} type='tel' maxLength='50' errors={errors}
            {...register('phone', {required: {value: true, message: captions[lang].errorMessages.mandatory.phone}, pattern: {value: /^[0-9 +-.]+$/i, message: captions[lang].errorMessages.invalid.phone}})} />
          <ContactFormInput name='position' lang={lang} type='text' maxLength='50' errors={errors}
            {...register('position', {required: {value: true, message: captions[lang].errorMessages.mandatory.position}})} />
          <ContactFormInput name='company' lang={lang} type='text' maxLength='50' errors={errors}
            {...register('company', {required: {value: true, message: captions[lang].errorMessages.mandatory.company}})} />
          <ContactFormInput name='numberEmployees' lang={lang} type='number' maxLength='10' errors={errors}
            {...register('numberEmployees', {required: {value: true, message: captions[lang].errorMessages.mandatory.numberEmployees}, min: 1, max: 999999})} />
          <div className='field mb-4'>
            <label htmlFor='country' className='label has-text-weight-normal is-size-5-desktop is-size-6-touch mb-1'>
              {captions[lang].labels.country} <span className='has-text-danger'>*</span>
            </label>
            <div className='control is-expanded'>
              <div className='select is-fullwidth'>
                <CountrySelector name='country' defaultValue='United Kingdom' {...register('country', {required: true})} />
              </div>
            </div>
          </div>
        </div>
        <div className='column is-half is-size-4-desktop is-size-5-touch'>
          <div className='field mb-4'>
            <label htmlFor='message' className='label has-text-weight-normal is-size-5-desktop is-size-6-touch mb-1'>
              {captions[lang].labels.message}
            </label>
            <div className='control'>
              <textarea className='textarea' {...register('message')} rows='10' />
            </div>
          </div>
          <div className='mb-4'>
            <h2 className='is-size-4-desktop is-size-5-touch'>
              {captions[lang].subtitle}
            </h2>
          </div>
          <div className='mb-4'>
            <h3 className='is-size-4-desktop is-size-5-touch'>
              {captions[lang].alternateContact} <Link to='mailto:info@approvol.com'>info@approvol.com</Link>
            </h3>
          </div>
        </div>
        <div className='column is-full centered'>
          <div className='is-size-6 mb-4'>
            {captions[lang].disclaimer1}
            <Link to={`${langUriPrefix}/privacy`}>{captions[lang].privacyPolicy}</Link>
            {captions[lang].disclaimer2}
          </div>
          <input type='submit' className='button is-info is-large has-button-shadow is-fullwidth' value={captions[lang].buttonSubmit} />
        </div>
      </div>
    </form>
  )
}

export default ContactForm
